import React, { useMemo, useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import rtlPlugin from 'stylis-plugin-rtl';
import { CacheProvider } from '@emotion/react';
import createCache from '@emotion/cache';
import { prefixer } from 'stylis';
import { AuthProvider } from './context/AuthContext';
import Header from './components/Header';
import ProtectedRoute from './components/ProtectedRoute';
import Home from './pages/Home';
import Admin from './pages/Admin';
import AdminMenuV2 from './pages/AdminMenuV2';
import AdminDashboard from './pages/AdminDashboard';
import ManagerDashboard from './pages/ManagerDashboard';
import RestaurantInfoForm from './pages/RestaurantInfoForm';
import Orders from './pages/Orders';
import POS from './pages/POS/POS';
import QRCodes from './pages/QRCodes';
import Menu from './pages/Menu';
import DemoManagerDashboard from './pages/DemoManagerDashboard';
import RestaurantInfo from './pages/RestaurantInfo';
import Login from './pages/Login';
import Register from './pages/Register';
import UserManagement from './pages/UserManagement';
import Profile from './pages/Profile';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import VerifyEmail from './pages/VerifyEmail';
import ResendVerification from './pages/ResendVerification';
import NotFound from './pages/NotFound';
import KitchenDisplay from './pages/Kitchen/KitchenDisplay';
import KitchenSettings from './pages/Kitchen/KitchenSettings';
import ThemeSettings from './pages/Manager/ThemeSettings';
import AnalyticsDashboard from './pages/Manager/AnalyticsDashboard';
import ServiceRequests from './pages/Manager/ServiceRequests';
import AdminAnalyticsDashboard from './pages/Admin/AdminAnalyticsDashboard';
import Subscription from './pages/Subscription';
import AdminSubscriptions from './pages/Admin/AdminSubscriptions';
import { Toaster } from './lib/toast';
import './i18n/i18n';
import './styles/global.css';

// Create RTL cache
const cacheRtl = createCache({
  key: 'muirtl',
  stylisPlugins: [prefixer, rtlPlugin],
});

// Create LTR cache
const cacheLtr = createCache({
  key: 'muiltr',
  stylisPlugins: [prefixer],
});

// Routes that render their own AppShell (sidebar + topbar). Append paths to this
// list as each page is converted in Phase 4 of the Mesoon refresh.
const APPSHELL_ROUTES = [
  /^\/admin\/dashboard(\/|$)/,
  /^\/admin\/subscriptions(\/|$)/,
  /^\/admin\/analytics(\/|$)/,
  /^\/admin\/orders(\/|$)/,
  /^\/admin\/qr-codes(\/|$)/,
  /^\/admin\/users(\/|$)/,
  /^\/admin\/menu-v2(\/|$)/,
  /^\/admin\/?$/,
  /^\/pos(\/|$)/,
  /^\/manager\/dashboard(\/|$)/,
  /^\/manager\/restaurant-info(\/|$)/,
  /^\/manager\/restaurant\/[^/]+\/(theme|analytics)(\/|$)/,
  /^\/profile(\/|$)/,
  /^\/subscription(\/|$)/,
];

function AppLayout({ direction, children }) {
  const location = useLocation();
  const isKitchenDisplay = /^\/kitchen\/[^/]+$/.test(location.pathname);
  const usesAppShell = APPSHELL_ROUTES.some((re) => re.test(location.pathname));
  const showGlobalHeader = !isKitchenDisplay && !usesAppShell;
  return (
    <div className="app" dir={direction}>
      {showGlobalHeader && <Header />}
      {children}
    </div>
  );
}

function AppContent() {
  const { i18n } = useTranslation();
  const [direction, setDirection] = useState(i18n.language === 'ar' ? 'rtl' : 'ltr');

  useEffect(() => {
    const handleLanguageChange = (lng) => {
      const newDirection = lng === 'ar' ? 'rtl' : 'ltr';
      setDirection(newDirection);
    };

    i18n.on('languageChanged', handleLanguageChange);
    
    // Set initial direction
    handleLanguageChange(i18n.language);

    return () => {
      i18n.off('languageChanged', handleLanguageChange);
    };
  }, [i18n]);

  const theme = useMemo(() => {
    const bodyFont = [
      'Inter',
      '"IBM Plex Sans Arabic"',
      '-apple-system',
      'BlinkMacSystemFont',
      '"Segoe UI"',
      'Roboto',
      '"Helvetica Neue"',
      'Arial',
      'sans-serif',
    ].join(',');
    const displayFont = [
      '"Plus Jakarta Sans"',
      '"IBM Plex Sans Arabic"',
      'Inter',
      '-apple-system',
      'sans-serif',
    ].join(',');
    return createTheme({
      direction,
      palette: {
        mode: 'light',
        primary: {
          main: '#0F5B45',
          light: '#1DB683',
          dark: '#0D4D3A',
          contrastText: '#ffffff',
        },
        secondary: {
          main: '#F59E0B',
          light: '#FBBF24',
          dark: '#B45309',
          // #F59E0B fails AA against white (2.12:1). Dark text on amber is 8.32:1.
          contrastText: '#0F172A',
        },
        error: { main: '#B91C1C' },
        warning: { main: '#B45309' },
        success: { main: '#047857' },
        info: { main: '#1D4ED8' },
        background: { default: '#FAFAF7', paper: '#FFFFFF' },
        text: { primary: '#0F172A', secondary: '#64748B' },
        divider: '#E5E7EB',
        mesoon: {
          sidebar: {
            bg: '#0F2D24',
            bgActive: '#0F5B45',
            bgHover: 'rgba(255, 255, 255, 0.06)',
            text: '#E2F1EA',
            textMuted: '#9DBDB1',
            border: 'rgba(255, 255, 255, 0.08)',
          },
          status: {
            active: { bg: '#D1FAE5', text: '#047857' },
            trial:  { bg: '#FEF3C7', text: '#B45309' },
            danger: { bg: '#FEE2E2', text: '#B91C1C' },
            neutral:{ bg: '#F3F4F6', text: '#4B5563' },
            info:   { bg: '#DBEAFE', text: '#1D4ED8' },
            progress:{ bg: '#FEF3C7', text: '#92400E' },
          },
          chart: ['#10B981', '#F59E0B', '#3B82F6', '#FBBF24', '#9CA3AF'],
        },
      },
      shape: { borderRadius: 12 },
      typography: {
        fontFamily: bodyFont,
        h1: { fontFamily: displayFont, fontWeight: 700, letterSpacing: '-0.02em' },
        h2: { fontFamily: displayFont, fontWeight: 700, letterSpacing: '-0.02em' },
        h3: { fontFamily: displayFont, fontWeight: 700, letterSpacing: '-0.02em' },
        h4: { fontFamily: displayFont, fontWeight: 600 },
        h5: { fontFamily: displayFont, fontWeight: 600 },
        h6: { fontFamily: displayFont, fontWeight: 600 },
        button: { fontWeight: 600, letterSpacing: 0 },
      },
      components: {
        MuiCssBaseline: {
          styleOverrides: {
            ':focus-visible': {
              outline: '2px solid #0F5B45',
              outlineOffset: 2,
            },
          },
        },
        MuiButton: {
          defaultProps: { disableElevation: true },
          styleOverrides: {
            root: {
              textTransform: 'none',
              borderRadius: 10,
              fontWeight: 600,
              padding: '10px 20px',
              transition: 'transform 180ms cubic-bezier(0.4,0,0.2,1), box-shadow 180ms cubic-bezier(0.4,0,0.2,1), background-color 180ms cubic-bezier(0.4,0,0.2,1)',
              '&:hover': {
                transform: 'translateY(-1px)',
                boxShadow: '0 4px 12px rgba(15, 91, 69, 0.18)',
              },
              '&:active': { transform: 'translateY(0)' },
              '&.Mui-disabled': { transform: 'none', boxShadow: 'none' },
            },
            containedPrimary: {
              boxShadow: '0 2px 8px rgba(15, 91, 69, 0.22)',
            },
            containedSecondary: {
              boxShadow: '0 2px 8px rgba(245, 158, 11, 0.22)',
            },
            outlined: {
              borderWidth: 1.5,
              '&:hover': { borderWidth: 1.5 },
            },
          },
          variants: [
            {
              props: { variant: 'pill' },
              style: {
                borderRadius: 9999,
                padding: '12px 28px',
                fontWeight: 600,
                background: 'linear-gradient(135deg, #0F5B45 0%, #0D4D3A 100%)',
                color: '#ffffff',
                boxShadow: '0 4px 14px rgba(15, 91, 69, 0.32)',
                '&:hover': {
                  transform: 'translateY(-1px)',
                  boxShadow: '0 6px 18px rgba(15, 91, 69, 0.40)',
                  background: 'linear-gradient(135deg, #1DB683 0%, #0F5B45 100%)',
                },
              },
            },
          ],
        },
        MuiOutlinedInput: {
          styleOverrides: {
            root: {
              borderRadius: 10,
              backgroundColor: '#ffffff',
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: '#E5E7EB',
                transition: 'border-color 150ms, box-shadow 150ms',
              },
              '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: '#9CA3AF',
              },
              '&.Mui-focused': {
                boxShadow: '0 0 0 4px rgba(15, 91, 69, 0.16)',
              },
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                borderColor: '#0F5B45',
                borderWidth: 1.5,
              },
              '&.Mui-error.Mui-focused': {
                boxShadow: '0 0 0 4px rgba(185, 28, 28, 0.15)',
              },
            },
          },
        },
        MuiInputLabel: {
          styleOverrides: {
            root: {
              '&.Mui-focused': { color: '#0F5B45' },
            },
          },
        },
        MuiCard: {
          styleOverrides: {
            root: {
              borderRadius: 16,
              border: '1px solid #EEF2F0',
              boxShadow: '0 1px 3px rgba(15, 23, 42, 0.04), 0 1px 2px rgba(15, 23, 42, 0.03)',
              backgroundImage: 'none',
            },
          },
        },
        MuiAppBar: {
          defaultProps: { color: 'inherit', elevation: 0 },
          styleOverrides: {
            root: {
              backgroundColor: '#FFFFFF',
              color: '#0F172A',
              borderBottom: '1px solid #E5E7EB',
              boxShadow: 'none',
            },
          },
        },
        MuiPaper: {
          styleOverrides: {
            root: { backgroundImage: 'none' },
          },
        },
        MuiChip: {
          styleOverrides: {
            root: {
              borderRadius: 9999,
              fontWeight: 500,
            },
          },
        },
        MuiDialog: {
          styleOverrides: {
            paper: {
              borderRadius: 20,
              boxShadow: '0 25px 60px rgba(15, 23, 42, 0.16)',
            },
          },
        },
        MuiDrawer: {
          styleOverrides: {
            paper: {
              backgroundColor: '#FFFFFF',
              backgroundImage: 'none',
              boxShadow: 'none',
            },
          },
        },
        MuiSkeleton: {
          styleOverrides: {
            root: {
              borderRadius: 12,
              backgroundColor: 'rgba(15, 23, 42, 0.06)',
            },
          },
        },
        MuiTooltip: {
          styleOverrides: {
            tooltip: {
              borderRadius: 8,
              fontSize: 12,
              padding: '6px 10px',
              backgroundColor: '#0F172A',
            },
          },
        },
      },
    });
  }, [direction]);

  const cache = direction === 'rtl' ? cacheRtl : cacheLtr;

  return (
    <CacheProvider value={cache}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <Toaster
          position="top-right"
          toastOptions={{
            duration: 4000,
            style: {
              background: 'var(--surface, #fff)',
              color: 'var(--text-primary, #0F172A)',
              border: '1px solid var(--border, #E5E7EB)',
              borderRadius: '12px',
              boxShadow: '0 4px 12px rgba(15, 23, 42, 0.08)',
            },
            success: {
              iconTheme: { primary: 'var(--success, #047857)', secondary: '#fff' },
            },
            error: {
              iconTheme: { primary: 'var(--error, #B91C1C)', secondary: '#fff' },
            },
          }}
        />
        <Router
          future={{
            v7_startTransition: true,
            v7_relativeSplatPath: true
          }}
        >
          <AppLayout direction={direction}>
            <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/verify-email" element={<VerifyEmail />} />
            <Route path="/resend-verification" element={<ResendVerification />} />
            
            {/* Protected Routes */}
            <Route 
              path="/admin/dashboard" 
              element={
                <ProtectedRoute requireAdmin={true}>
                  <AdminDashboard />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/manager/dashboard" 
              element={
                <ProtectedRoute>
                  <ManagerDashboard />
                </ProtectedRoute>
              } 
            />
            <Route
              path="/manager/restaurant-info"
              element={
                <ProtectedRoute>
                  <RestaurantInfoForm />
                </ProtectedRoute>
              }
            />
            <Route
              path="/manager/service-requests"
              element={
                <ProtectedRoute>
                  <ServiceRequests />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin"
              element={
                <ProtectedRoute>
                  <Admin />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/menu-v2"
              element={
                <ProtectedRoute>
                  <AdminMenuV2 />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/orders"
              element={
                <ProtectedRoute>
                  <Orders />
                </ProtectedRoute>
              }
            />
            <Route
              path="/pos"
              element={
                <ProtectedRoute>
                  <POS />
                </ProtectedRoute>
              }
            />
            <Route 
              path="/admin/analytics"
              element={
                <ProtectedRoute requireAdmin={true}>
                  <AdminAnalyticsDashboard />
                </ProtectedRoute>
              }
            />
            <Route 
              path="/admin/qr-codes" 
              element={
                <ProtectedRoute>
                  <QRCodes />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/admin/users" 
              element={
                <ProtectedRoute requireAdmin={true}>
                  <UserManagement />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/profile" 
              element={
                <ProtectedRoute>
                  <Profile />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/kitchen/:restaurantId" 
              element={
                <ProtectedRoute>
                  <KitchenDisplay />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/kitchen/:restaurantId/settings" 
              element={
                <ProtectedRoute>
                  <KitchenSettings />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/manager/restaurant/:restaurantId/theme" 
              element={
                <ProtectedRoute>
                  <ThemeSettings />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/manager/restaurant/:restaurantId/analytics" 
              element={
                <ProtectedRoute>
                  <AnalyticsDashboard />
                </ProtectedRoute>
              } 
            />
            
            <Route
              path="/subscription"
              element={
                <ProtectedRoute>
                  <Subscription />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/subscriptions"
              element={
                <ProtectedRoute requireAdmin={true}>
                  <AdminSubscriptions />
                </ProtectedRoute>
              }
            />

            {/* Public Routes */}
            <Route path="/menu/:restaurantId" element={<Menu />} />
            <Route path="/menu/:restaurantId/info" element={<RestaurantInfo />} />
            <Route path="/menu/:restaurantId/demo-dashboard" element={<DemoManagerDashboard />} />

            {/* 404 - catch-all */}
            <Route path="*" element={<NotFound />} />
          </Routes>
          </AppLayout>
      </Router>
      </ThemeProvider>
    </CacheProvider>
  );
}

function App() {
  return (
    <AuthProvider>
      <AppContent />
    </AuthProvider>
  );
}

export default App;
